import { useEffect, useId, useState } from 'react';
import styled from 'styled-components';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import CheckingBoxInverted from 'design-system/src/assets/icons/checkingBoxInverted.svg?react';
import DrugInverted from 'design-system/src/assets/icons/drugInverted.svg?react';
import AlarmInverted from 'design-system/src/assets/icons/alarmInverted.svg?react';
import NotificationInverted from 'design-system/src/assets/icons/notificationInverted.svg?react';
import TasksInverted from '../../assets/tasksInverted.svg?react';

export enum TableNullStateType {
  // used by all tables if there are no results and filters are applied
  NO_RESULTS_FROM_FILTERS = 'NO_RESULTS_FROM_FILTERS',
  // prescriptions table
  NO_PRESCRIPTIONS_OPEN = 'NO_PRESCRIPTIONS_OPEN',
  NO_PRESCRIPTIONS_AWAITING_OFFICE = 'NO_PRESCRIPTIONS_AWAITING_OFFICE',
  NO_PRESCRIPTIONS_AWAITING_PATIENT = 'NO_PRESCRIPTIONS_AWAITING_PATIENT',
  NO_PRESCRIPTIONS_COMPLETED = 'NO_PRESCRIPTIONS_COMPLETED',
  // tasks table
  NO_TASKS_OPEN = 'NO_TASKS_OPEN',
  NO_TASKS_SENT = 'NO_TASKS_SENT',
  NO_TASKS_COMPLETED = 'NO_TASKS_COMPLETED',
  // prior auth reviews table
  NO_PRIOR_AUTH_REVIEWS_OPEN = 'NO_PRIOR_AUTH_REVIEWS_OPEN',
  NO_PRIOR_AUTH_REVIEWS_REVIEWED = 'NO_PRIOR_AUTH_REVIEWS_REVIEWED',
  // notifications table
  NO_NOTIFICATIONS_OPEN = 'NO_NOTIFICATIONS_OPEN',
  NO_NOTIFICATIONS_COMPLETED = 'NO_NOTIFICATIONS_COMPLETED',
  // appeal reviews table
  NO_APPEAL_REVIEWS_OPEN = 'NO_APPEAL_REVIEWS_OPEN',
  NO_APPEAL_REVIEWS_REVIEWED = 'NO_APPEAL_REVIEWS_REVIEWED',
  // expiring prior auths table
  NO_EXPIRING_PRIOR_AUTHS_OPEN = 'NO_EXPIRING_PRIOR_AUTHS_OPEN',
  NO_EXPIRING_PRIOR_AUTHS_DISMISSED = 'NO_EXPIRING_PRIOR_AUTHS_DISMISSED',
}

type NullStateCopy = {
  title: string;
  description: string;
  icon: null | {
    element: React.ReactNode;
    iconColor: string;
    bgColor: string;
  };
};

const prescriptionsIconColor = 'var(--purple)';
const prescriptionsIconBgColor = 'var(--light-purple)';
const tasksIconColor = 'var(--dark-yellow)';
const tasksIconBgColor = 'var(--light-yellow)';
const priorAuthReviewsIconColor = 'var(--dark-green)';
const priorAuthReviewsIconBgColor = 'var(--light-green-3)';
const notificationsIconColor = 'var(--dark-blue)';
const notificationsIconBgColor = 'var(--light-blue)';
const appealReviewsIconColor = 'var(--dark-green)';
const appealReviewsIconBgColor = 'var(--light-green-3)';
const expiringPriorAuthsIconColor = 'var(--dark-red)';
const expiringPriorAuthsIconBgColor = 'var(--light-red)';

export const nullStateCopy: Record<TableNullStateType, NullStateCopy> = {
  [TableNullStateType.NO_RESULTS_FROM_FILTERS]: {
    title: 'No results found',
    description: 'Please check the tabs and filters and try again.',
    icon: null,
  },
  [TableNullStateType.NO_PRESCRIPTIONS_OPEN]: {
    title: 'No prescriptions currently in progress',
    description: 'Once a provider e-scribes a prescription to Tandem Access Support pharmacy, it will be found here.',
    icon: {
      element: <DrugInverted />,
      iconColor: prescriptionsIconColor,
      bgColor: prescriptionsIconBgColor,
    },
  },
  [TableNullStateType.NO_PRESCRIPTIONS_AWAITING_OFFICE]: {
    title: "You're all caught up!",
    description:
      'Prescriptions awaiting office input will be found here. You can see open and completed prescriptions in other tabs.',
    icon: {
      element: <DrugInverted />,
      iconColor: prescriptionsIconColor,
      bgColor: prescriptionsIconBgColor,
    },
  },
  [TableNullStateType.NO_PRESCRIPTIONS_AWAITING_PATIENT]: {
    title: 'No prescriptions awaiting patient input',
    description:
      'Prescriptions awaiting patient input will be found here. You can see open and completed prescriptions in other tabs.',
    icon: {
      element: <DrugInverted />,
      iconColor: prescriptionsIconColor,
      bgColor: prescriptionsIconBgColor,
    },
  },
  [TableNullStateType.NO_PRESCRIPTIONS_COMPLETED]: {
    title: 'No completed prescriptions found',
    description:
      'Completed prescriptions that have been sent to the pharmacy will be found here. You can see open prescriptions in other tabs.',
    icon: {
      element: <DrugInverted />,
      iconColor: prescriptionsIconColor,
      bgColor: prescriptionsIconBgColor,
    },
  },
  [TableNullStateType.NO_TASKS_OPEN]: {
    title: "You're all caught up!",
    description:
      'Tasks requiring your response will be found here. You can see tasks awaiting Tandem and closed tasks in other tabs.',
    icon: {
      element: <TasksInverted />,
      iconColor: tasksIconColor,
      bgColor: tasksIconBgColor,
    },
  },
  [TableNullStateType.NO_TASKS_SENT]: {
    title: 'No tasks awaiting Tandem found',
    description:
      'Tasks awaiting response from Tandem will be found here. You can see tasks awaiting office and closed tasks in other tabs.',
    icon: {
      element: <TasksInverted />,
      iconColor: tasksIconColor,
      bgColor: tasksIconBgColor,
    },
  },
  [TableNullStateType.NO_TASKS_COMPLETED]: {
    title: 'No closed tasks found',
    description: 'Tasks that are closed will be found here. You can see open tasks in other tabs.',
    icon: {
      element: <TasksInverted />,
      iconColor: tasksIconColor,
      bgColor: tasksIconBgColor,
    },
  },
  [TableNullStateType.NO_PRIOR_AUTH_REVIEWS_OPEN]: {
    title: 'No PA reviews found',
    description: 'PAs that require office review will be found here.',
    icon: {
      element: <CheckingBoxInverted />,
      iconColor: priorAuthReviewsIconColor,
      bgColor: priorAuthReviewsIconBgColor,
    },
  },
  [TableNullStateType.NO_PRIOR_AUTH_REVIEWS_REVIEWED]: {
    title: 'No reviewed PAs found',
    description: 'PAs that your team has reviewed will be found here.',
    icon: {
      element: <CheckingBoxInverted />,
      iconColor: priorAuthReviewsIconColor,
      bgColor: priorAuthReviewsIconBgColor,
    },
  },
  [TableNullStateType.NO_NOTIFICATIONS_OPEN]: {
    title: 'No notifications found',
    description: 'Notifications such as updates to prescriptions or enrollment statuses will be found here.',
    icon: {
      element: <NotificationInverted />,
      iconColor: notificationsIconColor,
      bgColor: notificationsIconBgColor,
    },
  },
  [TableNullStateType.NO_NOTIFICATIONS_COMPLETED]: {
    title: 'No completed notifications found',
    description: 'Notifications marked as completed will be found here.',
    icon: {
      element: <NotificationInverted />,
      iconColor: notificationsIconColor,
      bgColor: notificationsIconBgColor,
    },
  },
  [TableNullStateType.NO_APPEAL_REVIEWS_OPEN]: {
    title: 'No appeal reviews found',
    description: 'Appeals that require office review will be found here.',
    icon: {
      element: <CheckingBoxInverted />,
      iconColor: appealReviewsIconColor,
      bgColor: appealReviewsIconBgColor,
    },
  },
  [TableNullStateType.NO_APPEAL_REVIEWS_REVIEWED]: {
    title: 'No reviewed appeal reviews found',
    description: 'Appeals that your team has reviewed will be found here.',
    icon: {
      element: <CheckingBoxInverted />,
      iconColor: appealReviewsIconColor,
      bgColor: appealReviewsIconBgColor,
    },
  },
  [TableNullStateType.NO_EXPIRING_PRIOR_AUTHS_OPEN]: {
    title: 'No expiring PAs found',
    description: 'PAs that expire in the next 30 days will be found here.',
    icon: {
      element: <AlarmInverted />,
      iconColor: expiringPriorAuthsIconColor,
      bgColor: expiringPriorAuthsIconBgColor,
    },
  },
  [TableNullStateType.NO_EXPIRING_PRIOR_AUTHS_DISMISSED]: {
    title: 'No dismissed expiring PAs found',
    description: 'PAs that expire in the next 30 days that your team has dismissed will be found here.',
    icon: {
      element: <AlarmInverted />,
      iconColor: 'var(--dark-red)',
      bgColor: 'var(--light-red)',
    },
  },
};

const StyledTableNullState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const StyledTableNullStateIcon = styled.div<{ $bgColor: string; $iconColor: string }>`
  display: flex;
  width: 48px;
  height: 48px;
  justify-content: center;
  align-items: center;
  background-color: ${({ $bgColor }) => $bgColor};
  border-radius: 50%;

  svg {
    width: 23px;
    height: 23px;
    color: ${({ $iconColor }) => $iconColor};
  }
`;

const StyledTableNullStateText = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 4px;
  width: 360px;
  color: var(--black);
`;

export type TableNullStateProps = {
  nullStateType: TableNullStateType;
  onClearFilters?: () => void;
};

/**
 * A component that displays a null state for a table.
 */
export const TableNullState = ({ nullStateType, onClearFilters }: TableNullStateProps) => {
  const [heightReady, setHeightReady] = useState(false);
  const containerId = useId();

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (container) {
      // NOTE: this logic assumes that this is the last element in the page
      container.style.height = `calc(100vh - ${container.getBoundingClientRect().top}px)`;
    }
    // We need to wait for the height to be ready before rendering the component to avoid a flash of
    // the content at the top of the page before it moves to the vertical center of the page
    setHeightReady(true);
  }, []);

  return (
    <StyledTableNullState id={containerId}>
      {heightReady && (
        <>
          {nullStateCopy[nullStateType].icon && (
            <StyledTableNullStateIcon
              $bgColor={nullStateCopy[nullStateType].icon?.bgColor ?? ''}
              $iconColor={nullStateCopy[nullStateType].icon?.iconColor ?? ''}
            >
              {nullStateCopy[nullStateType].icon?.element ?? ''}
            </StyledTableNullStateIcon>
          )}
          <StyledTableNullStateText>
            <Typography styledAs="bodyDMSans" weight="600">
              {nullStateCopy[nullStateType].title}
            </Typography>
            <Typography styledAs="bodyDMSans">{nullStateCopy[nullStateType].description}</Typography>
          </StyledTableNullStateText>

          {nullStateType === TableNullStateType.NO_RESULTS_FROM_FILTERS && onClearFilters && (
            <Button onClick={onClearFilters} variant="secondary" size="sm">
              Clear filters
            </Button>
          )}
        </>
      )}
    </StyledTableNullState>
  );
};

export default TableNullState;
